import { useEffect, useMemo, useRef, useState } from 'react';
import './DateInput.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parseYmd(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplay(value) {
  const dt = parseYmd(value);
  if (!dt) return '';
  return dt.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function buildCells(viewYear, viewMonth) {
  const first = new Date(viewYear, viewMonth, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Mobile-safe date picker — calendar stays within parent width.
 * Native <input type="date"> popups overflow in Chrome device mode.
 */
export default function DateInput({
  value = '',
  onChange,
  placeholder = 'Select date',
  className = '',
  size = 'md',
  'aria-label': ariaLabel = 'Date',
}) {
  const selected = parseYmd(value);
  const initial = selected || new Date();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const dt = parseYmd(value) || new Date();
    setViewYear(dt.getFullYear());
    setViewMonth(dt.getMonth());
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const cells = useMemo(() => buildCells(viewYear, viewMonth), [viewYear, viewMonth]);
  const todayYmd = toYmd(new Date());

  const shiftMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const pickDay = (day) => {
    if (!day) return;
    const ymd = toYmd(new Date(viewYear, viewMonth, day));
    onChange(ymd);
    setOpen(false);
  };

  return (
    <div
      className={[
        'date-input',
        open ? 'is-open' : '',
        size === 'sm' ? 'date-input--sm' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      ref={rootRef}
    >
      <button
        type="button"
        className="date-input-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`date-input-value text-truncate ${value ? '' : 'is-placeholder'}`}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        <i className="bi bi-calendar3" />
      </button>

      {open && (
        <div className="date-input-panel" role="dialog" aria-label="Choose date">
          <div className="date-input-nav">
            <button type="button" className="date-input-nav-btn" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <i className="bi bi-chevron-left" />
            </button>
            <div className="date-input-month">
              {MONTHS[viewMonth]} {viewYear}
            </div>
            <button type="button" className="date-input-nav-btn" onClick={() => shiftMonth(1)} aria-label="Next month">
              <i className="bi bi-chevron-right" />
            </button>
          </div>

          <div className="date-input-weekdays">
            {WEEKDAYS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>

          <div className="date-input-grid">
            {cells.map((day, idx) => {
              if (!day) return <span key={`e-${idx}`} className="date-input-day is-empty" />;
              const ymd = toYmd(new Date(viewYear, viewMonth, day));
              const isSelected = ymd === value;
              const isToday = ymd === todayYmd;
              return (
                <button
                  key={ymd}
                  type="button"
                  className={[
                    'date-input-day',
                    isSelected ? 'is-selected' : '',
                    isToday ? 'is-today' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => pickDay(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="date-input-footer">
            <button
              type="button"
              className="btn btn-link btn-sm text-decoration-none px-0"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm text-decoration-none px-0"
              onClick={() => {
                onChange(todayYmd);
                setOpen(false);
              }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
